import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InvestorLayout from '../components/InvestorLayout';
import investorService from '../services/investorService';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Building2, MapPin, Calendar, FileText, AlertCircle } from 'lucide-react';

const STATUS_TABS = [
  'all',
  'interested',
  'offer_made',
  'negotiating',
  'contract_signed',
  'completed',
  'cancelled',
];

const InvestorPortfolioPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  // Status badge renkleri (DashboardPage ile aynı)
  const getStatusColor = (status) => {
    const colors = {
      interested: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      offer_made: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
      negotiating: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      contract_signed: 'bg-green-100 text-green-700 hover:bg-green-200',
      deposit_paid: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
      completed: 'bg-green-600 text-white hover:bg-green-700',
      cancelled: 'bg-red-100 text-red-700 hover:bg-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // KKTC izin durumu badge renkleri
  const getPermitColor = (status) => {
    const colors = {
      not_required: 'bg-gray-100 text-gray-700',
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Tapu durumu badge renkleri
  const getTapuColor = (status) => {
    const colors = {
      not_started: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const fetchInvestments = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await investorService.getInvestments(user.id);

      if (fetchError) throw fetchError;

      setInvestments(data || []);
    } catch (err) {
      console.error('Portfolio fetch error:', err);
      setError(err.message || t('investor.dashboard.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [user?.id, t]);

  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  // Tab'a göre yatırımları filtrele
  const filteredInvestments =
    activeTab === 'all'
      ? investments
      : investments.filter((inv) => inv.status === activeTab);

  // Skeleton bileşeni
  const InvestmentCardSkeleton = () => (
    <Card className="border border-gray-200">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <Skeleton className="w-full sm:w-32 h-24 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
          </div>
          <div className="space-y-2 text-right">
            <Skeleton className="h-6 w-24 ml-auto" />
            <Skeleton className="h-4 w-20 ml-auto" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Tek bir yatırım kartı
  const InvestmentCard = ({ investment }) => {
    const property = investment.property;
    const thumbnail = property?.images?.[0] || null;

    return (
      <Card className="border border-gray-200 hover:border-[#D4AF37] hover:shadow-md transition-all duration-300">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Sol: Thumbnail */}
            <div className="w-full sm:w-32 h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={property?.title || 'Property'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                  <Building2 size={32} className="text-gray-400" />
                </div>
              )}
            </div>

            {/* Orta: Bilgiler */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[#0A192F] text-lg truncate">
                {property?.title || 'Property'}
              </h3>
              <div className="flex items-center text-sm text-gray-500 mt-1">
                <MapPin size={14} className="mr-1 flex-shrink-0" />
                <span className="truncate">{property?.location || '-'}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                {property?.price && (
                  <span className="font-semibold text-[#0A192F]">
                    £{Number(property.price).toLocaleString()}
                  </span>
                )}
                {investment.offer_amount && (
                  <span className="text-gray-600">
                    {t('investor.portfolio.offerAmount')}:{' '}
                    <span className="font-medium text-[#D4AF37]">
                      £{Number(investment.offer_amount).toLocaleString()}
                    </span>
                  </span>
                )}
              </div>

              {/* KKTC İzin & Tapu Durumu */}
              <div className="flex flex-wrap gap-2 mt-2">
                {investment.kktc_permit_status && (
                  <Badge variant="outline" className={getPermitColor(investment.kktc_permit_status)}>
                    <FileText size={12} className="mr-1" />
                    {t('investor.portfolio.kktcPermit')}:{' '}
                    {t(`investor.permit_status.${investment.kktc_permit_status}`)}
                  </Badge>
                )}
                {investment.tapu_status && (
                  <Badge variant="outline" className={getTapuColor(investment.tapu_status)}>
                    <FileText size={12} className="mr-1" />
                    {t('investor.portfolio.tapuStatus')}:{' '}
                    {t(`investor.tapu_status.${investment.tapu_status}`)}
                  </Badge>
                )}
              </div>
            </div>

            {/* Sağ: Status & Tarih */}
            <div className="flex flex-row sm:flex-col items-start sm:items-end justify-between sm:justify-start gap-2 flex-shrink-0">
              <Badge className={getStatusColor(investment.status)}>
                {t(`investor.status.${investment.status}`)}
              </Badge>
              <div className="flex items-center text-xs text-gray-500">
                <Calendar size={12} className="mr-1" />
                {new Date(investment.updated_at).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Alt: Notlar & Aksiyon butonları */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            {investment.notes && (
              <p className="text-sm text-gray-600 mb-3">
                <span className="font-medium text-gray-700">{t('investor.portfolio.notes')}:</span>{' '}
                {investment.notes}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white"
                onClick={() => navigate(`/tr/properties/${investment.property_id}`)}
              >
                {t('investor.portfolio.viewDetails')}
              </Button>
              {!['completed', 'cancelled'].includes(investment.status) && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white"
                  >
                    {t('investor.portfolio.editInvestment')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    {t('investor.portfolio.cancelInvestment')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (error) {
    return (
      <InvestorLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <AlertCircle size={48} className="text-red-500 mb-4" />
          <h2 className="text-2xl font-heading text-[#0A192F] mb-2">
            {t('investor.dashboard.error_title')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            onClick={fetchInvestments}
            className="bg-[#0A192F] text-white hover:bg-[#112240]"
          >
            {t('investor.dashboard.retry')}
          </Button>
        </div>
      </InvestorLayout>
    );
  }

  return (
    <InvestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading text-[#0A192F] mb-2">
            {t('investor.portfolio.title')}
          </h1>
          <p className="text-gray-600">
            {t('investor.portfolio.lastUpdated')}:{' '}
            {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Filtre Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-gray-100 p-1 rounded-lg">
            {STATUS_TABS.map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white text-sm px-3 py-1.5"
              >
                {tab === 'all'
                  ? t('investor.portfolio.all')
                  : t(`investor.status.${tab}`)}
                {tab !== 'all' && (
                  <span className="ml-1.5 text-xs opacity-70">
                    ({investments.filter((inv) => inv.status === tab).length})
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Yatırım Listesi - Tüm tab'lar aynı içerik, filtre state ile kontrol */}
          <div className="mt-6 space-y-4">
            {loading ? (
              <>
                <InvestmentCardSkeleton />
                <InvestmentCardSkeleton />
                <InvestmentCardSkeleton />
              </>
            ) : filteredInvestments.length > 0 ? (
              filteredInvestments.map((investment) => (
                <InvestmentCard key={investment.id} investment={investment} />
              ))
            ) : (
              <Card className="border border-gray-200">
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <Building2 size={56} className="text-gray-300 mb-4" />
                  <h3 className="text-xl font-heading text-[#0A192F] mb-2">
                    {t('investor.portfolio.noInvestments')}
                  </h3>
                  <Button
                    className="mt-4 bg-[#D4AF37] text-white hover:bg-[#B5952F]"
                    onClick={() => navigate('/tr')}
                  >
                    {t('investor.portfolio.startInvesting')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </Tabs>
      </div>
    </InvestorLayout>
  );
};

export default InvestorPortfolioPage;
